/**
 * AI registry client — PROVIDER# / MODEL# / ROLE# / CONFIG rows.
 *
 * Backend: pos-agents-service, built by Plan 3 Task 11.
 *   GET /agents/admin/ai-registry
 *   PUT /agents/admin/ai-registry/{rowType}/{rowId}
 *
 * The registry is the ONLY place a provider or model is chosen. A vendor swap
 * is one ROLE# row edit here — burnRate and minCreditsPerCall live on the role
 * precisely so that swapping the vendor changes OUR cost and never the
 * tenant's charge (spec A2).
 */

import { adminGet, adminSend } from './transport';

export const AI_ROLE_IDS = [
  'fast-text', 'deep-text', 'vision', 'grounding', 'image-gen', 'video-gen',
] as const;
export type AiRoleId = (typeof AI_ROLE_IDS)[number];

/**
 * Role ids still shown on the legacy "Role routing" card list (2026-10-04,
 * Ruby Generation Engine Task 16). `image-gen` and `video-gen` are retired
 * from that UI — image generation is now routed per step through the Ruby
 * AI Actions routing map (ActionsTab / RoutingMap), and video has no action
 * yet. The ROLE# rows themselves still exist in the registry (a caller not
 * yet converted to an action still falls back to them) — only the card list
 * is narrowed.
 */
export const LEGACY_ROUTING_ROLE_IDS: readonly AiRoleId[] = AI_ROLE_IDS.filter(
  (id) => id !== 'image-gen' && id !== 'video-gen',
);

export const AI_ROLE_LABELS: Record<AiRoleId, string> = {
  'fast-text': 'Fast text',
  'deep-text': 'Deep text',
  vision: 'Vision',
  grounding: 'Search grounding',
  'image-gen': 'Image generation',
  'video-gen': 'Video generation',
};

/** What each role is FOR, shown under the title on the Routing tab. */
export const AI_ROLE_HINTS: Record<AiRoleId, string> = {
  'fast-text': 'High-volume, low-complexity text. Point this at the cheapest capable model.',
  'deep-text': 'Reasoning-heavy work. The expensive model belongs here and nowhere else.',
  vision: 'Image understanding and product recognition.',
  grounding: 'Web search grounding for factual lookups.',
  'image-gen': 'Image generation for Creator Studio and product media.',
  'video-gen': 'Video generation for Creator Studio.',
};

export interface AiUnitPricesDto {
  input_per_million_usd?: number;
  output_per_million_usd?: number;
  cache_write_per_million_usd?: number;
  cache_read_per_million_usd?: number;
  per_image_usd?: number;
  per_call_usd?: number;
  per_second_usd?: number;
  [key: string]: number | undefined;
}

export interface AiProviderDto {
  providerId: string;
  label: string;
  vendor: string;
  enabled: boolean;
  /** SSM SecureString PATH. Never a decrypted value (spec §9.2). */
  credentialsRef: string;
  /** Four-character tail supplied by the backend, e.g. "••••EFGH". */
  credentialHint?: string | null;
  authType?: 'header' | 'query' | 'serviceAccount';
  endpointTemplate?: string;
  updatedAt?: string;
}

/** Typical usage counts for a model, for cost-preview UIs (2026-10-04). */
export interface AiUsageCountsDto {
  inputTokens?: number;
  outputTokens?: number;
  cacheWriteTokens?: number;
  cacheReadTokens?: number;
  images?: number;
  calls?: number;
  seconds?: number;
}

/**
 * Where a model is actually routed from — one entry per action step
 * (primary or fallback), vibe override or role. Only present on rows
 * returned by the `/agents/admin/ai/catalog` endpoint (`withUsedBy`); the
 * plain registry endpoint does not enrich this, so callers compute a
 * client-side fallback when it is absent (see CapabilityGrid.tsx).
 */
export interface AiModelUseDto {
  type: 'action' | 'vibe' | 'role';
  id: string;
  step?: string;
  actionId?: string;
  use: 'primary' | 'fallback' | 'override';
}

export interface AiModelDto {
  providerId: string;
  modelId: string;
  label: string;
  enabled: boolean;
  capabilities: string[];
  unitPrices: AiUnitPricesDto;
  lastSyncedAt?: string;
  lastVerifiedAt?: string;
  /** Set by the drift cron when the synced price differs from the stored one. */
  driftPercent?: number;
  /** Vendor model version/snapshot id, e.g. "2025-08". Informational. */
  version?: string;
  /** Typical usage for this model, for cost-preview UIs. */
  typicalUsage?: AiUsageCountsDto;
  /** Server-computed "used by" — see AiModelUseDto. */
  usedBy?: AiModelUseDto[];
}

export interface AiRoleDto {
  roleId: string;
  label: string;
  enabled: boolean;
  providerId: string;
  modelId: string;
  fallbackProviderId?: string;
  fallbackModelId?: string;
  burnRate: number;
  minCreditsPerCall: number;
  reserveCap: number;
  updatedAt?: string;
}

export interface AiConfigHistoryEntryDto {
  at: string;
  by: string;
  byEmail?: string;
  changes: Array<{ field: string; from: number | string | boolean | null; to: number | string | boolean | null }>;
}

export interface AiRegistryConfigDto {
  creditUnitPriceUsd: number;
  minPurchaseCredits: number;
  // --- 2026-10-17 live Ruby Token value (all optional; absent = defaults) ---
  /** USD value of ONE Ruby Token (default 0.00001). */
  usdPerRubyToken?: number;
  /** 0.01 / usdPerRubyToken (default 1000). */
  tokensPerCredit?: number;
  tokensPerUsd?: number;
  /** Multiplier used when an action / item has none (default 1.5). */
  defaultMultiplier?: number;
  updatedAt?: string;
  updatedBy?: string;
  history?: AiConfigHistoryEntryDto[];
}

export interface AiRegistryResponse {
  providers: AiProviderDto[];
  models: AiModelDto[];
  roles: AiRoleDto[];
  config: AiRegistryConfigDto;
}

export type RegistryRowType = 'provider' | 'model' | 'role' | 'config';

/** The registry sort key for a model row, minus the `MODEL#` prefix. */
export function modelKey(providerId: string, modelId: string): string {
  return `${providerId}#${modelId}`;
}

/**
 * Path segment for PUT /agents/admin/ai-registry/{rowType}/{rowId}.
 *
 * A model row id carries a '#', which cannot travel raw in a path segment —
 * it is percent-encoded here and split on '#' by the handler. Missing ids
 * throw rather than producing a URL that would PUT to the wrong row.
 */
export function registryRowId(
  rowType: RegistryRowType,
  ids: { providerId?: string; modelId?: string; roleId?: string },
): string {
  if (rowType === 'config') return 'CONFIG';
  if (rowType === 'provider') {
    if (!ids.providerId) throw new Error('registryRowId: providerId is required for a provider row');
    return encodeURIComponent(ids.providerId);
  }
  if (rowType === 'role') {
    if (!ids.roleId) throw new Error('registryRowId: roleId is required for a role row');
    return encodeURIComponent(ids.roleId);
  }
  if (!ids.providerId) throw new Error('registryRowId: providerId is required for a model row');
  if (!ids.modelId) throw new Error('registryRowId: modelId is required for a model row');
  return encodeURIComponent(modelKey(ids.providerId, ids.modelId));
}

export function modelsForProvider(models: AiModelDto[], providerId: string): AiModelDto[] {
  return models.filter((m) => m.providerId === providerId);
}

/** A model with no positive unit price cannot be metered, so it must not be selectable. */
export function isModelPriced(model: AiModelDto): boolean {
  return Object.values(model.unitPrices).some((v) => typeof v === 'number' && v > 0);
}

export async function getAiRegistry(): Promise<AiRegistryResponse> {
  const body = await adminGet<Partial<AiRegistryResponse>>('/agents/admin/ai-registry');
  return {
    providers: body.providers ?? [],
    models: body.models ?? [],
    roles: body.roles ?? [],
    config: body.config ?? { creditUnitPriceUsd: 0.01, minPurchaseCredits: 50 },
  };
}

export async function putRegistryRow<T = unknown>(
  rowType: RegistryRowType,
  rowId: string,
  patch: Record<string, unknown>,
): Promise<T> {
  return adminSend<T>(`/agents/admin/ai-registry/${rowType}/${rowId}`, 'PUT', patch);
}
